import type { EnsureFreshSession, EnsureFreshSessionOptions } from './auth-transport.types'

class AuthTransport {
	private ensureFreshSession: EnsureFreshSession | null = null

	public configure(ensureFreshSession: EnsureFreshSession): void {
		this.ensureFreshSession = ensureFreshSession
	}

	public reset(): void {
		this.ensureFreshSession = null
	}

	public async getAccessToken(options?: EnsureFreshSessionOptions): Promise<string | null> {
		return this.ensureFreshSession?.(options) ?? null
	}

	public async requireAccessToken(): Promise<string> {
		const accessToken = await this.getAccessToken()
		if (!accessToken) {
			throw new Error('Active auth session is missing')
		}
		return accessToken
	}

	public async fetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
		const accessToken = await this.requireAccessToken()
		const request = new Request(input, init)
		const response = await fetch(this.authorizeRequest(request, accessToken))

		if (response.status !== 401) {
			return response
		}

		let retryAccessToken: string | null
		try {
			retryAccessToken = await this.getAccessToken({
				forceRefresh: true,
				rejectedAccessToken: accessToken,
			})
		} catch {
			return response
		}

		if (!retryAccessToken) {
			return response
		}
		if (!this.isSafeReplayMethod(request.method)) {
			return response
		}

		let replayAccessToken: string
		try {
			// Revalidate immediately before the replay. A logout may have won after
			// refresh returned but before the second network request is dispatched.
			replayAccessToken = await this.requireAccessToken()
		} catch {
			return response
		}

		return fetch(this.authorizeRequest(request, replayAccessToken))
	}

	private isSafeReplayMethod(method: string): boolean {
		return ['GET', 'HEAD', 'OPTIONS'].includes(method.toUpperCase())
	}

	private authorizeRequest(request: Request, accessToken: string): Request {
		const headers = new Headers(request.headers)
		headers.set('Authorization', `Bearer ${accessToken}`)

		const timezone = this.getClientTimezone()
		if (timezone) {
			headers.set('X-Timezone', timezone)
		}

		return new Request(request.clone(), { headers })
	}

	private getClientTimezone(): string | null {
		try {
			return Intl.DateTimeFormat().resolvedOptions().timeZone || null
		} catch {
			return null
		}
	}
}

export const authTransport = new AuthTransport()

export const authorizedFetch = (input: RequestInfo | URL, init?: RequestInit): Promise<Response> =>
	authTransport.fetch(input, init)
